import { FC, Fragment, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { ActionButton } from 'app/components/Button/ActionButton';
import Image from 'app/components/Image';
import ModalDefault from 'app/components/Modal/ModalDefault';
import { TippyDefault } from 'app/components/Tippy';
import Typography from 'app/components/Typography';

import { formatDate, sanitizeHtml } from 'app/helpers';
import { useBoolean } from 'app/hooks';
import { isEmpty, isNil } from 'lodash';

import { acknowledgeAlert, changeStatusAlert, getTotalUnreadAlert, resolveAlert } from './core/request';
import { Alert, AlertStatus } from './core/type';
import { useAlertStore } from './store';
import { KTCard } from 'theme/helpers';

const statusStyle: Record<AlertStatus, { backgroundColor: string; color: string; label: string }> = {
  [AlertStatus.New]: {
    backgroundColor: 'var(--danger-light-2)',
    color: 'var(--danger)',
    label: 'New',
  },
  [AlertStatus.Acknowledged]: {
    backgroundColor: 'var(--warning-light-2)',
    color: 'var(--warning)',
    label: 'Acknowledged',
  },
  [AlertStatus.Resolved]: {
    backgroundColor: 'var(--success-light-2)',
    color: 'var(--success)',
    label: 'Resolved',
  },
};

const StatusPill = styled.div<{ bg: string; color: string }>`
  width: fit-content;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-radius: 99px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  background-color: ${({ bg }) => bg};
  color: ${({ color }) => color};
`;

const ActionLink = styled.span`
  color: var(--primary);
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  margin-left: 12px;
  :hover {
    text-decoration: underline;
  }
`;

const Content = styled.div<{ $isRead: boolean }>`
  flex: 1 1 0;

  .card:hover {
    cursor: ${({ $isRead }) => ($isRead ? 'default' : 'pointer')};
  }
  .stockrun-link {
    color: var(--primary);
    cursor: ${({ $isRead }) => ($isRead ? 'default' : 'pointer')};
    z-index: 9;
    font-weight: 600;
    text-decoration: underline;
    :hover {
      text-decoration: underline !important;
    }
  }
`;

const Point = styled.div`
  width: 10px;
  height: 10px;
  background: var(--danger);
  border-radius: 50%;
  margin-right: 8px;
`;

const ORPHANED_DEVICE_NOTE = 'Device no longer registered in inventory.';

interface AlertItemProps {
  alert: Alert;
  isShowImg?: boolean;
  // True when the device this alert refers to no longer matches any current
  // inventory row (renamed, deleted, or moved org) - nothing left to mute, so
  // resolving is the only available action.
  isOrphaned?: boolean;
}

export const AlertItem: FC<AlertItemProps> = ({ alert, isShowImg = false, isOrphaned = false }) => {
  const contentRef = useRef<any>();
  const isTooltipVisible = useBoolean(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmittingResolve, setIsSubmittingResolve] = useState(false);

  const queryClient = useQueryClient();

  const status = alert.status ?? AlertStatus.New;

  const updateAlertInQueryCache = (id: number, patch: Partial<Alert>) => {
    queryClient
      .getQueryCache()
      .findAll(['alerts'])
      .forEach((query) => {
        const data: any = query.state.data;

        if (data && data.pages) {
          queryClient.setQueryData(query.queryKey, {
            ...data,
            pages: data.pages.map((page: any) => ({
              ...page,
              items: page.items?.map((item: Alert) =>
                item.id === id ? { ...item, ...patch } : item,
              ),
            })),
          });
        }
      });
  };

  const onAcknowledgeAlert = (event: React.MouseEvent) => {
    event.stopPropagation();

    acknowledgeAlert(alert.id).then((response) => {
      if (response) {
        useAlertStore.getState().onAcknowledgeAlert(alert.id);
        updateAlertInQueryCache(alert.id, { status: AlertStatus.Acknowledged });
      }
    });
  };

  const onOpenResolveModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setResolutionNote(isOrphaned ? ORPHANED_DEVICE_NOTE : '');
    setIsResolveModalOpen(true);
  };

  const onSubmitResolveAlert = async () => {
    setIsSubmittingResolve(true);

    const response = await resolveAlert(alert.id, resolutionNote || undefined);

    setIsSubmittingResolve(false);

    if (response) {
      useAlertStore.getState().onResolveAlert(alert.id, resolutionNote || undefined);
      updateAlertInQueryCache(alert.id, {
        status: AlertStatus.Resolved,
        resolutionNote: resolutionNote || undefined,
      });
      setIsResolveModalOpen(false);
    }
  };

  const onChangeStatusAlert = (id: number, isRead: boolean) => async () => {
    if (isRead) return;

    const response = await changeStatusAlert(id);

    if (response) {
      useAlertStore.getState().onChangeReadAlert(id);

      queryClient
        .getQueryCache()
        .findAll(['alerts'])
        .forEach((query) => {
          const data: any = query.state.data;

          if (data && data.pages) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              pages: data.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((item: Alert) =>
                  item.id === id ? { ...item, is_read: true } : item,
                ),
              })),
            });
          }
        });

      getTotalUnreadAlert();
    }
  };

  return (
    <Fragment>
    <Content
      $isRead={alert.is_read}
      onClick={onChangeStatusAlert(alert.id, alert.is_read)}
      ref={contentRef}
      onMouseEnter={() => {
        isTooltipVisible.setValue(true);
      }}
      onMouseLeave={() => {
        isTooltipVisible.setValue(false);
      }}
    >
      <TippyDefault
        arrow={false}
        placement="top"
        content={!alert.is_read ? `Mark as read` : ''}
        delay={[300, 0]}
        visible={isTooltipVisible.value && !alert.is_read}
      >
        <KTCard
          contentStyle={{
            padding: '24px 28px 24px 10px',
            backgroundColor: alert.is_read ? '#FFFFFF' : 'hsl(345.34deg 86.27% 60% / 7%)',
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <div className="d-flex align-items-start">
                <div className="d-flex align-items-center">
                  {!alert.is_read && (
                    <div className="mr-2" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Point
                        className="point"
                        style={{
                          marginRight:
                            isEmpty(alert.content.image) && !isNil(alert.content.image) ? 56 : '',
                        }}
                      ></Point>
                    </div>
                  )}

                  {isShowImg && (
                    <Image
                      src={alert.content.image!}
                      alt="Product"
                      width={56}
                      height={56}
                      radius={8}
                      style={{
                        marginRight: !alert.is_read ? 18 : 0,
                        marginLeft: alert.is_read ? 18 : 0,
                      }}
                    />
                  )}

                  <span
                    style={{
                      marginRight:
                        isEmpty(alert.content.image) && !isNil(alert.content.image) ? 50 : '',
                    }}
                  />

                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(alert.content.description),
                    }}
                    style={{
                      paddingLeft: alert.is_read ? 18 : 0,
                      paddingRight: 16,
                      fontSize: 16,
                      lineHeight: '26px',
                    }}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center mt-2" style={{ paddingLeft: alert.is_read ? 18 : 0 }}>
                <StatusPill bg={statusStyle[status].backgroundColor} color={statusStyle[status].color}>
                  {statusStyle[status].label}
                </StatusPill>

                {status === AlertStatus.New && (
                  <ActionLink onClick={onAcknowledgeAlert}>Acknowledge</ActionLink>
                )}

                {status !== AlertStatus.Resolved && (
                  <ActionLink onClick={onOpenResolveModal}>Resolve</ActionLink>
                )}

                {isOrphaned && status !== AlertStatus.Resolved && (
                  <Typography variant="body-4" color="danger" style={{ marginLeft: 12 }}>
                    Device no longer registered — nothing to mute, consider resolving
                  </Typography>
                )}

                {status === AlertStatus.Resolved && alert.resolutionNote && (
                  <Typography variant="body-4" color="neutral-6" style={{ marginLeft: 12 }}>
                    Note: {alert.resolutionNote}
                  </Typography>
                )}
              </div>
            </div>
            <Typography variant="body-4" style={{ minWidth: 140 }}>
              {formatDate(alert.createdAt, 'dd MMM yyyy')} at{' '}
              {formatDate(alert.createdAt, 'H:mm:ss')}
            </Typography>
          </div>
        </KTCard>
      </TippyDefault>
    </Content>

    {isResolveModalOpen && (
      <ModalDefault
        title="Resolve alert"
        onClickCloseModal={() => setIsResolveModalOpen(false)}
      >
        <Typography variant="body-3" style={{ marginBottom: 8, display: 'block' }}>
          Resolution note (optional)
        </Typography>
        <textarea
          className="form-control form-control-solid"
          rows={3}
          value={resolutionNote}
          onChange={(event) => setResolutionNote(event.target.value)}
          placeholder="Describe how this alert was resolved"
        />
        <ActionButton
          onSubmit={onSubmitResolveAlert}
          onCancel={() => setIsResolveModalOpen(false)}
          labelButtonSubmit="Resolve"
          isSubmitting={isSubmittingResolve}
        />
      </ModalDefault>
    )}
    </Fragment>
  );
};
